import React, { useState } from 'react';
import './DataTableStagesHost.scss';
import {
    DataRequestProject,
    DataRequestProjectState,
} from '../../../models/DataRequestHub/DataRequestProject';
import DataTableStagesList from '../DataTableStagesList/DataTableStagesList';
import { useNavigate } from 'react-router-dom';
import { DataTableForm } from '../DataTableForm/DataTableForm';
import { DesignModeSwitcher } from '../DesignModeSwitcher/DesignModeSwitcher';
import { DataTableEditorModeEnum } from '../../Admin/DataRequest/ProjectEditorHost/ProjectEditorHost';
import { EditorTypeEnum } from '../../../models/DataRequestHub/ProjectEditorEnum';
import { EditorModeEnum } from '../../../models/DataRequestHub/ProjectEditorModeEnum';
import { SnapshotsPopup } from '../SnapshotsPopup/SnapshotsPopup';
import { DataTable } from '../../../models/DataRequestHub/DataTable';
import { FreezePanesSettings } from '../../../models/DataRequestHub/slices/models/freeze-panes-settings.model';
import { PopupsWizard } from '../../../components/PopupsWizard/PopupsWizard';
import { DataTableFormPopup } from '../DataTableFormPopup/DataTableFormPopup';
import { useDataRequestUrl } from '../../../hooks/useDataRequestDashboardUrl';
import { useIsDataRequestProjectReadonly } from '../../../custom-hooks/useIsDataRequestProjectReadonly';

interface DataTableStagesHostProps {
    projects: DataRequestProject[];
    isAdminView: boolean;
    canUseDataTools: boolean;
    updateProjectsData(isInitialFetch?: boolean): void;
    preselectedTableId: number;
    preselectedTableViewId: number;
    markCellsAsSeen(projectId: number, tableId: number, viewId: number): void;
    updateColumnWidth?(tableId: number, columnId: string, width: number): void;
    updateFreezePanes?(tableId: number, viewId: number, settings: FreezePanesSettings): void;
    updateDataTableFreezePanes?(tableId: number, settings: FreezePanesSettings): void;
    areTableNamesClickable?: boolean;
    areActionButtonsHidden?: boolean;
    hideSnapshots?: boolean;
    isReadonlyMode?: boolean;
    engagementId?: number;
}

const DataTableStagesHost = (props: DataTableStagesHostProps) => {
    const navigate = useNavigate();
    const isReadonlyProject = useIsDataRequestProjectReadonly(props.projects, props.engagementId) || !!props.isReadonlyMode;
    const [selectedDataTableId, setSelectedDataTableId] = useState<number>(
        props.preselectedTableId
    );
    const [selectedTableViewId, setSelectedTableViewId] = useState<number>(
        props.preselectedTableViewId
    );

    const [isSnapshotsPopupVisible, setIsSnapshotsPopupVisible] =
        useState<boolean>(false);

    const [isArchiveSnapshotsPopupMode, setIsArchiveSnapshotsPopupMode] =
        useState<boolean>(false);
    const [selectedTablePopup, setSelectedTablePopup] = useState<DataTable>();
    const [selectedTableViewPopupId, setSelectedTableViewPopupId] =
        useState<number>(0);
    const [originalTablePopupId, setOriginalTablePopupId] = useState<number>(0);
    const [originalTableViewPopupId, setOriginalTableViewPopupId] =
        useState<number>(0);
    const editorUrl = useDataRequestUrl(true);

    const enableDesignMode = (viewId: number) => {
        const targetProject = getProjectByViewId(viewId);

        if (targetProject) {
            navigate(`/${editorUrl}`, {
                state: {
                    dataTableEditorParams: {
                        targetTableId: selectedDataTableId,
                        targetTableViewId: selectedTableViewId,
                    },
                    dataTableEditorMode: selectedTableViewId
                        ? DataTableEditorModeEnum.EditView
                        : DataTableEditorModeEnum.Table,
                    project: targetProject,
                    shouldNavigateToProjectViewMode: true,
                    type: EditorTypeEnum.DataTable,
                    mode: EditorModeEnum.EditProject,
                    additionalUrlToReturn: `?tab=${
                        EditorTypeEnum.DataTable + 1
                    }`,
                },
            });
        }
    };

    const getProjectByViewId = (viewId: number) =>
        props.projects.find((project) =>
            project.dataTables.some((table) =>
                table.views.some((view) => view.id === viewId)
            )
        ) ?? props.projects.find((project) => project);

    const getDataTable = (tableId: number) => {
        const result = props.projects
            .flatMap((x) => x.dataTables)
            .find((f) => f.id === tableId);

        return result;
    };

    const getTableView = (viewId: number) => {
        const allDataTables = props.projects.flatMap((x) => x.dataTables);
        const allViews = allDataTables.flatMap((x) => x.views);
        const result = allViews.find((x) => x.id === viewId);
        return result;
    };

    const renderStagesList = () => (
        <DataTableStagesList
            onTableViewNameClick={(tableId: number, viewId: number) => {
                turnOnTableView(tableId, viewId);
            }}
            onTableNameClick={(tableId) => {
                turnOnTableView(tableId);
            }}
            onArchiveClick={(tableId: number, viewId: number) => {
                openArhivePopup(tableId, viewId);
            }}
            enableDesignMode={enableDesignMode}
            isEditorMode={false}
            isTemplateEditorView={false}
            invalidTableStageIds={[]}
            invalidTableViewIds={[]}
            {...props}
        />
    );

    const openArhivePopup = (tableId: number, viewId: number) => {
        setOriginalTablePopupId(tableId);
        setOriginalTableViewPopupId(viewId);
        setIsSnapshotsPopupVisible(true);
        setIsArchiveSnapshotsPopupMode(true);
    };

    const turnOnTableView = (tableId: number, selectedViewId?: number) => {
        setSelectedDataTableId(tableId);
        setSelectedTableViewId(selectedViewId);
    };

    const turnOnStagesList = () => {
        setSelectedDataTableId(null);
        setSelectedTableViewId(null);
    };

    const isProjectActive = (tableId: number) => {
        const targetProject =
            props.projects.find((project) =>
                project.dataTables.some((table) => table.id === tableId)
            ) ?? props.projects.find((project) => project);

        return (
            targetProject.state === DataRequestProjectState.Active &&
            !isReadonlyProject
        );
    };

    const renderView = () => {
        const targetProject = getProjectByViewId(selectedTableViewId);
        const targetTable = getDataTable(selectedDataTableId);
        const targetView = getTableView(selectedTableViewId);

        return (
            <>
                <div className="data-table-stages-host-header">
                    <div className="stage-navigation-breadcrumb">
                        <label
                            className="link-text"
                            onClick={() => turnOnStagesList()}>
                            Data Tables
                        </label>
                        <label className="node-separator">{' / '}</label>
                        <label
                            className="link-text"
                            onClick={() => turnOnStagesList()}>
                            {targetTable.name}
                        </label>
                        {selectedTableViewId ? (
                            <>
                                <label className="node-separator">
                                    {' / '}
                                </label>
                                <label className="link-text">
                                    {targetView.name}
                                </label>
                            </>
                        ) : (
                            <></>
                        )}
                    </div>
                    {(!props.hideSnapshots || props.isAdminView) && (
                        <div className="right-header">
                            {!props.hideSnapshots && (
                                <div
                                    className="link-text"
                                    onClick={() => {
                                        setIsSnapshotsPopupVisible(true);
                                        setIsArchiveSnapshotsPopupMode(false);
                                        setOriginalTablePopupId(targetTable.id);
                                        setOriginalTableViewPopupId(
                                            targetView?.id
                                        );
                                    }}>
                                    Data Snapshots
                                </div>
                            )}
                            {props.isAdminView &&
                                isProjectActive(targetTable.id) && (
                                    <DesignModeSwitcher
                                        defaultValue={false}
                                        onEnable={enableDesignMode}
                                        formId={0}
                                    />
                                )}
                        </div>
                    )}
                </div>

                <DataTableForm
                    isAdminView={props.isAdminView}
                    canUseDataTools={props.canUseDataTools}
                    projectId={targetProject.id}
                    variables={targetProject.variables}
                    dataTable={targetTable}
                    selectedTableViewId={selectedTableViewId}
                    updateProjectsData={props.updateProjectsData}
                    markCellsAsSeen={props.markCellsAsSeen}
                    updateColumnWidth={props.updateColumnWidth}
                    updateFreezePanes={props.updateFreezePanes}
                    updateDataTableFreezePanes={props.updateDataTableFreezePanes}
                    isReadonlyMode={props.isReadonlyMode}
                    project={targetProject}
                    areActionButtonsHidden={props.areActionButtonsHidden}
                />
            </>
        );
    };

    const renderSnapshotPopup = () => {
        const targetProject = getProjectByViewId(originalTableViewPopupId);
        const targetTable = getDataTable(originalTablePopupId);
        const targetView = getTableView(originalTableViewPopupId);

        let popupHeader =
            targetView != null
                ? `${targetTable.name}: ${targetView.name}`
                : targetTable.name;
        popupHeader += isArchiveSnapshotsPopupMode
            ? '  View History'
            : ' - Data Snapshots';

        let snapshotFileName = `${targetProject.practiceName} - ${targetTable.name}`;

        if (targetView != null) {
            snapshotFileName = `${snapshotFileName} - ${targetView.name}`;
        }

        const columnWidthOverrides = targetView
            ? targetView.viewColumns
                  .filter((vc) => vc.width != null)
                  .map((vc) => ({
                      columnId: vc.customColumnId,
                      width: vc.width,
                  }))
            : targetTable.columns
                  .filter((col) => col.width != null)
                  .map((col) => ({
                      columnId: col.customColumnId,
                      width: col.width,
                  }));

        return (
            <>
                <PopupsWizard
                    popups={[
                        <SnapshotsPopup
                            projectId={targetProject.id}
                            objectId={targetTable.id}
                            viewId={targetView?.id}
                            header={popupHeader}
                            setSelectedTable={setSelectedTablePopup}
                            setSelectedTableViewId={setSelectedTableViewPopupId}
                            onClose={() => setIsSnapshotsPopupVisible(false)}
                            isArchiveMode={isArchiveSnapshotsPopupMode}
                            snapshotFileName={snapshotFileName}
                            canUseDataTools={props.canUseDataTools}
                            isReadonlyMode={isReadonlyProject}
                            mode="table"
                        />,

                        <DataTableFormPopup
                            selectedDataTable={selectedTablePopup}
                            selectedTableViewId={selectedTableViewPopupId}
                            project={targetProject}
                            header={popupHeader}
                            isAdminView={props.isAdminView}
                            isReadonlyMode={true}
                            isFullScreenButtonHidden={
                                props.areActionButtonsHidden
                            }
                            columnWidthOverrides={columnWidthOverrides}
                        />,
                    ]}
                />
            </>
        );
    };

    const render = () => {
        return (
            <>
                {selectedDataTableId || selectedTableViewId
                    ? renderView()
                    : renderStagesList()}{' '}
                {!props.hideSnapshots && isSnapshotsPopupVisible
                    ? renderSnapshotPopup()
                    : []}
            </>
        );
    };

    return <div className="data-table-stages-host">{render()}</div>;
};

export default DataTableStagesHost;
